import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { useIsMobile } from "@/hooks/useMediaQuery"
import {
  SheetRoot,
  SheetTrigger,
  SheetClose,
  SheetTitle,
  SheetDescription,
  SheetShell,
  SheetOverlay,
  splitSheetClass,
} from "@/components/mobile/SheetShell"

/**
 * Responsive dialog.
 *
 * Desktop / tablet landscape: the classic centered Radix dialog.
 * Phones (< 768px): the very same API renders a native-feeling bottom sheet
 * (grab handle, swipe-to-dismiss, safe-area aware, content scrolls inside).
 * Every modal of the app gets this for free — no call-site changes needed.
 */
const SheetModeContext = React.createContext(false)

const Dialog = ({ dismissible = true, ...props }) => {
  const isMobile = useIsMobile()
  if (isMobile) {
    return (
      <SheetModeContext.Provider value={true}>
        <SheetRoot dismissible={dismissible} {...props} />
      </SheetModeContext.Provider>
    )
  }
  return <DialogPrimitive.Root {...props} />
}

const DialogTrigger = React.forwardRef((props, ref) => {
  const sheet = React.useContext(SheetModeContext)
  const Comp = sheet ? SheetTrigger : DialogPrimitive.Trigger
  return <Comp ref={ref} {...props} />
})
DialogTrigger.displayName = "DialogTrigger"

const DialogPortal = DialogPrimitive.Portal

const DialogClose = React.forwardRef((props, ref) => {
  const sheet = React.useContext(SheetModeContext)
  const Comp = sheet ? SheetClose : DialogPrimitive.Close
  return <Comp ref={ref} {...props} />
})
DialogClose.displayName = "DialogClose"

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => {
  const sheet = React.useContext(SheetModeContext)
  if (sheet) return <SheetOverlay ref={ref} className={className} {...props} />
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      {...props} />
  )
})
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const sheet = React.useContext(SheetModeContext)
  if (sheet) {
    const { hideClose, flush, inner, outer } = splitSheetClass(className)
    return (
      <SheetShell
        ref={ref}
        flush={flush}
        showClose={!hideClose}
        className={outer}
        bodyClassName={cn("grid content-start gap-4", inner)}
        {...props}>
        {children}
      </SheetShell>
    )
  }
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        {...props}>
        {children}
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => {
  const sheet = React.useContext(SheetModeContext)
  return (
    <div
      className={cn(
        "flex flex-col space-y-1.5 text-center sm:text-left",
        // In a sheet the title sits left, leaving room for the round close button.
        sheet && "pr-10 text-left",
        className
      )}
      {...props} />
  )
}
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => {
  const sheet = React.useContext(SheetModeContext)
  return (
    <div
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        // The primary action stays reachable above the home indicator while the form scrolls.
        sheet &&
          "sticky bottom-[calc(var(--sheet-pb)*-1)] z-10 -mx-[var(--sheet-px)] border-t border-stone-100 bg-background px-[var(--sheet-px)] pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] pt-3 shadow-[0_-10px_14px_-12px_rgba(0,0,0,0.14)]",
        className
      )}
      {...props} />
  )
}
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => {
  const sheet = React.useContext(SheetModeContext)
  const Comp = sheet ? SheetTitle : DialogPrimitive.Title
  return (
    <Comp
      ref={ref}
      className={cn("text-lg font-semibold leading-none tracking-tight", className)}
      {...props} />
  )
})
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => {
  const sheet = React.useContext(SheetModeContext)
  const Comp = sheet ? SheetDescription : DialogPrimitive.Description
  return (
    <Comp
      ref={ref}
      className={cn("text-sm text-muted-foreground", className)}
      {...props} />
  )
})
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
